"use client";

// packages/eden-markdown/src/index.jsx
import { Children } from "react";
import styled, { css } from "styled-components";
import RM from "markdown-to-jsx";
import { Image } from "@churchofjesuschrist/eden-image";
import { Ordered, Unordered, Item } from "@churchofjesuschrist/eden-list";
import { CodeBlock as EdenCode } from "@churchofjesuschrist/eden-code-block";
import { Text4 } from "@churchofjesuschrist/eden-text";
import {
  H1 as EdenH1,
  H2 as EdenH2,
  H3 as EdenH3,
  H4 as EdenH4,
  H5 as EdenH5,
  H6 as EdenH6
} from "@churchofjesuschrist/eden-headings";
import {
  spacing32,
  spacing64,
  spacing16,
  backgroundColorAlt,
  spacing4,
  spacing8,
  fontSize16,
  borderRadius
} from "@churchofjesuschrist/eden-style-constants";
import { BlockQuote } from "@churchofjesuschrist/eden-block-quote";
import {
  Table,
  THead,
  TBody,
  TFoot,
  TD,
  TR,
  TH
} from "@churchofjesuschrist/eden-table";
import { jsx } from "react/jsx-runtime";
var ReactMarkdown = styled(RM)`
  & > :first-child {
    margin-block-start: 0;
  }

  & > :last-child {
    margin-block-end: 0;
  }
`;
var BlockCode = styled(EdenCode)`
  margin: ${spacing32} 0;
`;
var InlineCode = styled.code`
  display: inline;
  background-color: ${backgroundColorAlt};
  padding: calc(${spacing4} / 2) ${spacing4};
  border-radius: ${borderRadius};
`;
var headingStyles = css`
  margin-block-start: ${spacing32};
  margin-block-end: ${spacing16};
`;
var H1 = styled(EdenH1)`
  margin-block-start: ${spacing8};
  margin-block-end: ${spacing32};
`;
var H2 = styled(EdenH2)`
  margin-block-start: ${spacing64};
  margin-block-end: ${spacing16};
`;
var H3 = styled(EdenH3)`
  ${headingStyles}
`;
var H4 = styled(EdenH4)`
  ${headingStyles}
`;
var H5 = styled(EdenH5)`
  ${headingStyles}
`;
var H6 = styled(EdenH6)`
  ${headingStyles}
`;
var P = styled(Text4).attrs({ as: "p" })`
  font-size: ${fontSize16};
  margin: ${spacing16} 0;
`;
var THWithScope = ({ ...props }) => /* @__PURE__ */ jsx(TH, { scope: "col", ...props });
var Pre = ({ children, ...props }) => {
  const {
    props: { children: code = "", className = "" }
  } = Children.only(children);
  const language = /lang-(\w+)/.exec(className)?.[1];
  return /* @__PURE__ */ jsx(BlockCode, { language, ...props, children: code.trim() });
};
var Markdown = ({ components = {}, children, ...props }) => /* @__PURE__ */ jsx(
  ReactMarkdown,
  {
    options: {
      overrides: {
        blockquote: BlockQuote,
        code: InlineCode,
        pre: Pre,
        h1: H1,
        h2: H2,
        h3: H3,
        h4: H4,
        h5: H5,
        h6: H6,
        img: Image,
        li: Item,
        ol: Ordered,
        p: P,
        ul: Unordered,
        table: Table,
        thead: THead,
        tbody: TBody,
        tfoot: TFoot,
        th: THWithScope,
        td: TD,
        tr: TR,
        ...components
      }
    },
    ...props,
    children
  }
);
Markdown.displayName = "Markdown";
export {
  Markdown
};
